<template src="./app.html"></template>

<script>
//get hostname
const hostname = window.location.hostname;
const comic_mode = hostname.includes('mikomic');

import tool_handlers from './modules/tools.js';
import ColorPickerWrapper from './components/ColorPickerWrapper.vue';
import { Vue3ColorPicker } from '@cyhnkckali/vue3-color-picker';

//! Mixins are imported here
import mixin_preferences from './modules/mixins/preferences.js';
import mixin_ui from './modules/mixins/ui.js';
import mixin_canvas from './modules/mixins/canvas.js';
import mixin_document from './modules/mixins/document.js';
import mixin_divider_tool from './modules/mixins/divider_tool.js';
import mixin_balloon_tool from './modules/mixins/balloon_tool.js';
import mixin_layers from './modules/mixins/layers.js';
import mixin_language from './modules/mixins/language.js';
import mixin_user from './modules/mixins/user.js';
import mixin_text from './modules/mixins/text.js';
import mixin_style_manager from './modules/mixins/style_manager.js';
import mixin_cookie_consent from './modules/mixins/cookie_consent.js';
import mixin_auth from './modules/mixins/auth.js';
import mixin_gallery from './modules/mixins/gallery_mixin.js';
import mixin_subscription from './modules/mixins/subscription.js';
import mixin_ai_generation from './modules/mixins/ai_generation.js';
import mixin_ai_library from './modules/mixins/ai_library.js';


export default {
    name: 'app',
    components: {
        ColorPickerWrapper,
        Vue3ColorPicker
    },
    mixins: [        
        mixin_preferences,
        mixin_language,
        mixin_ui,
        mixin_canvas,
        mixin_document,
        mixin_divider_tool,
        mixin_balloon_tool,
        mixin_layers,        
        mixin_user,
        mixin_text,
        mixin_style_manager,
        mixin_cookie_consent,
        mixin_auth,
        mixin_gallery,
        mixin_subscription,
        mixin_ai_generation,
        mixin_ai_library,
    ],
    data() {
        return {
            comic_mode: comic_mode,
            mikogen_landing_visible: true, // Flag to control Mikogen landing page visibility
            performance_balancer: false,

            // App initialization
            app_initializing: true,

            // Maintenance mode
            maintenance_mode: false,

            // Browser compatibility
            browser_info: {
                name: 'unknown',
                supports_filesystem_api: false,
                is_firefox: false,
                is_safari: false,
                is_chromium: false
            },

            // System tools
            active_tool: 'select_panel', 
            active_subtool: null,
            previous_tool: null,
            previous_object: null,
            is_space_pan_active: false,          
            
            text_transformer: null, 
            performing_wheel : false,
            _zoom_update_pending: false, // Flag for rAF zoom throttling
            _last_wheel_event: null, // Store the last wheel event for rAF processing
            _handle_outside_interaction: null, // Store reference for removal
            _handle_keydown: null, // Store reference for removal
            _handle_keyup: null, // Store reference for removal

            _middle_mouse_global_handler: null, // Store reference for middle mouse global handler
            middle_mouse_drag_state: { // State for middle mouse drag functionality
                is_dragging: false,
                last_pos: null
            },

            // Pages navigation bar visibility
            show_pages_navigation: false,
            
            // Cross-panel dragging option
            enable_cross_panel_dragging: false,
            
        }
    },

    computed: {
        mikogen_generator_active() {
            return !this.comic_mode && !this.mikogen_landing_visible;
        },

        selected_object_exists() {
            return !!this.selected_object;
        },

        selected_object_text() {
            return (this.selected_object && this.selected_object.text) ? this.selected_object.text.text() : '';
        },

        selected_object_editing_mode() {
            return this.selected_object ? this.selected_object.getAttr('editing_mode') : false;
        },

        selected_object_balloon_type() {
            return this.selected_object ? this.selected_object.getAttr('balloon_type') : '';
        },
        
        selected_object_shape_stroke() {
            return this.selected_object && this.selected_object.shape ? this.selected_object.shape.stroke() : '#000000';
        },
        
        selected_object_text_short() {
            if (this.selected_object && this.selected_object.text) {
                const txt = this.selected_object.text.text();
                return txt.length > 20 ? txt.substring(0, 20) + '...' : txt;
            }
            return 'Edit text';
        },
        selected_object_is_balloon() {
            return this.selected_object && this.selected_object.getAttr('object_type') === 'balloon';
        },

        selected_object_is_standalone_text() {
            return this.selected_object && this.selected_object.getAttr('object_type') === 'standalone_text';
        },

        selected_object_is_in_global_panel() {
            if (!this.selected_object) return false;
            
            // Find which panel contains this object
            const parent_panel = this.find_parent_panel_for_object(this.selected_object);
            return parent_panel && parent_panel.getAttr('is_global') === true;
        },

        is_saving_status() {
            return this.status_message === this.get_t('status.saving');
        },
       
        
    },

    created() {
        // Mikogen: Check local storage for auth status to skip landing page immediately
        if (!this.comic_mode) {
            const auth_status = localStorage.getItem('mikogen_auth_status');
            if (auth_status === 'true') {
                this.mikogen_landing_visible = false;
            }
        }
    },

    mounted() {

        if (this.comic_mode === false) {            
            window.addEventListener('resize', () => {
                document.body.style.width = window.innerWidth + 'px';
            });
        }
        
        this.initialize_app_sequence();
        

        const container = document.getElementById('canvas_wrapper');
        if (!container && this.comic_mode === true) {
            console.error("Canvas wrapper not found!");
            return;
        }

        if (this.comic_mode === true) { 
            // Use ResizeObserver for reliable initialization after layout is stable
            const resize_observer = new ResizeObserver(entries => {
                for (const entry of entries) {
                    // Check for a minimum reasonable size before initializing
                    if (entry.contentRect.width > 100 && entry.contentRect.height > 100) {
                        resize_observer.disconnect(); // Stop observing once initialized
                        this.initialize_canvas();
                        break; // Initialize only once
                    }
                }
            });
            resize_observer.observe(container);

            window.addEventListener('resize', this.handle_window_resize);

            // Setup centralized hotkey system
            this.setup_hotkeys();

            // Setup page reload prevention
            this.setup_page_reload_prevention();

            // Detect browser and filesystem API support
            this.detect_browser_compatibility();

            // Add listener to close menus on outside click - updated to use centralized method
            document.addEventListener('pointerdown', this.handle_global_click_for_menus);

            // Initialize IndexedDB
            this.init_indexed_db();

            // Check for pending autosave
            this.check_pending_autosave();
            
            // Load recent files
            this.load_recent_files();
        }

        setTimeout(() => {
            this.$nextTick(() => {
                this.refresh_lucide();
            });
        }, 200);
        
    },

    updated() {
        this.refresh_lucide();
    },

    beforeUnmount() {
        // Remove listener when component is destroyed - updated to match the new method name
        document.removeEventListener('pointerdown', this.handle_global_click_for_menus);

        // Remove page reload prevention listeners
        if (this.beforeunload_handler) {
            window.removeEventListener('beforeunload', this.beforeunload_handler);
        }
        if (this.keydown_handler) {
            document.removeEventListener('keydown', this.keydown_handler);
        }

        // Stop autosave timer
        if (this.autosave_interval) {
            clearInterval(this.autosave_interval);
        }
        // Remove other listeners using stored references
        window.removeEventListener('resize', this.handle_window_resize);
        if (this._handle_outside_interaction) { // Check if reference exists
             document.removeEventListener('mousedown', this._handle_outside_interaction);
             document.removeEventListener('touchstart', this._handle_outside_interaction); // Remove touchstart listener
        }
        // Remove middle mouse global handler if it exists
        if (this._middle_mouse_global_handler) {
            document.removeEventListener('mouseup', this._middle_mouse_global_handler);
        }
        // Ensure stage listeners are removed if stage exists
        if (this.stage) {
            this.stage.destroy(); // Konva recommends destroying the stage on unmount
        }
        // Remove hotkey listeners using stored references
        if (this._handle_keydown) {
            document.removeEventListener('keydown', this._handle_keydown);
        }
        if (this._handle_keyup) {
            document.removeEventListener('keyup', this._handle_keyup);
        }
    },


    watch: {
        // Mikogen: Auto-skip landing on login
        'user_data.account_status': function(new_val) {
            if (!this.comic_mode && new_val === 'active') {
                this.mikogen_landing_visible = false;
            }
        },
        

        // Watch for changes in selected_panel to clear selected_layer
        selected_panel(new_panel, old_panel) {
            if (new_panel !== old_panel) {
                this.selected_layer = null;
                // Update layers displayed in the panel
                //this.update_panel_layers();               
            }
        },
        // Watch for changes in selected_object to update UI elements if needed
        selected_object(new_object, old_object) {
            if (new_object !== old_object) {
                this.update_ui_from_selected_object(); // Update sliders/values on selection change
            }
             this.$nextTick(() => {
                 this.refresh_lucide(); // Refresh icons when selection changes UI
             });
        }
    },

    directives: {
        lucide: {
            mounted(el) {
                if (window.lucide) {
                    lucide.createIcons();
                }
            },
            updated(el) {
                if (window.lucide) {
                    lucide.createIcons();
                }
            }
        },
        click_away: {
            mounted(el, binding) {
                el.click_away_event = (event) => {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener('click', el.click_away_event);
            },
            unmounted(el) {
                document.removeEventListener('click', el.click_away_event);
            }
        }
    },

    methods: {

        open_lightbox(element) {
            console.log("element", element);
            const img = element.querySelector('img');
            const lightbox = document.getElementById('niji_lightbox');
            const lightbox_img = document.getElementById('niji_lightbox_img');
            
            lightbox_img.src = img.src;
            lightbox.style.display = 'flex';
        },



        //! App initialization
        async initialize_app_sequence() {
            try {
                this.load_language_from_preferences();

                // Load user data first (critical - blocks UI)
                await this.check_user_status(this.current_language);
                
                // Mikogen: Auto-skip landing if logged in
                if (!this.comic_mode && this.user_data && this.user_data.account_status === 'active') {
                    this.mikogen_landing_visible = false;
                }
                

                // User data loaded - show UI immediately
                this.app_initializing = false;

                // Load everything else in parallel (non-blocking)
                await Promise.all([
                    this.check_maintenance_mode(),
                    this.check_premium_status(),
                    this.load_pricing_config(),
                    this.check_dev_environment()
                ]);

                this.check_cookie_consent();
                this.init_subscription_data();
                this.ai_panel_ready = true;
                
                // Load gallery categories if in dev environment
                if (this.is_dev_environment && this.comic_mode === true) {
                    this.load_gallery_categories();
                }
                
                // Refresh Lucide icons after everything is loaded
                this.$nextTick(() => {
                    // Start of Mikogen fix: Delay execution to ensure DOM is fully rendered
                    setTimeout(() => {
                        this.refresh_lucide();
                    }, 1000);
                });
                
            } catch (error) {
                console.error('Error during app initialization:', error);
                this.app_initializing = false;
            }
        },

        async check_maintenance_mode() {
            try {
                const form_data = new FormData();
                form_data.append('request', 'get_maintenance_status');
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data) {
                    this.maintenance_mode = response.data.maintenance_mode || false;
                } else {
                    this.maintenance_mode = false;
                }
            } catch (error) {
                console.error("Error checking maintenance mode:", error);
                this.maintenance_mode = false;
            }
        },

        //! Manage Tools
        change_tool(tool_id, deactivate_tool = true) {
            if (!this.comic_mode) return; // Only allow tool changes in comic mode
            // Deactivate current tool
            if (deactivate_tool && this.active_tool && tool_handlers[this.active_tool] && tool_handlers[this.active_tool].deactivate) {
                tool_handlers[this.active_tool].deactivate(this);
            }

            // Clean UI
            this.exit_balloon_edit_mode(); // Exit balloon edit mode if active
            
            // Reset subtool
            this.deselect_subtool();
            
            // Store the new active tool
            this.active_tool = tool_id;
            
            // Clear object selection when changing tools (but keep panel selection)
            this.deselect_object();
            
            // Don't deselect panel here - this is what was causing the panel deselection issue
            
            // Activate new tool
            if (tool_handlers[tool_id] && tool_handlers[tool_id].activate) {
                tool_handlers[tool_id].activate(this);
            }
            
        },

        activate_default_tool() {
            // Activate the default tool specified in data()
            if (this.active_tool && tool_handlers[this.active_tool] && tool_handlers[this.active_tool].activate) {
                tool_handlers[this.active_tool].activate(this);
            }
        },

        change_subtool(subtool, deselect_object = true) {
            this.deselect_subtool();     
            if (deselect_object) {
                this.deselect_object();
            }   

            this.exit_panel_edit_mode();
            this.active_subtool = subtool;
            
            if (subtool === 'divider' && this.selected_panel) {                          
                this.setup_divider_events();
            }
        },


        deselect_subtool() {
            // Existing logic
            this.cancel_divider_drawing(); // Assuming this handles divider cleanup
            this.active_subtool = null;
        },

        temporarily_activate_pan() {

            // Before deactivate
            if (this.previous_tool == 'select') {
                this.previous_object = this.selected_object;
            }

            // Deactivate current tool
            const old_handler = tool_handlers[this.active_tool];
            if (old_handler && old_handler.deactivate) {
                old_handler.deactivate(this);
            }
            
            // Set active tool to hand/pan without updating previous_tool
            this.active_tool = 'hand';
            
            // Activate pan tool
            const new_handler = tool_handlers['hand'];
            if (new_handler && new_handler.activate) {
                new_handler.activate(this);
            }

            
        },        

        restore_previous_tool() {
            if (!this.previous_tool) return;
            
            // Deactivate pan tool
            const old_handler = tool_handlers[this.active_tool];
            if (old_handler && old_handler.deactivate) {
                old_handler.deactivate(this);
            }
            
            // Restore previous tool
            this.active_tool = this.previous_tool;
            
            // Activate previous tool
            const new_handler = tool_handlers[this.active_tool];
            if (new_handler && new_handler.activate) {
                new_handler.activate(this);
            }
            
            // Clear previous tool reference
            this.previous_tool = null;

            if (this.active_tool == 'select') {
                this.select_object(this.previous_object);
            }
        },

        //! Global
        refresh_lucide() {
            this.$nextTick(() => {
                if (window.lucide) {
                    lucide.createIcons();
                }
            });
        },

        setup_event_listeners() {
            if (!this.comic_mode) return; // Only setup event listeners in comic mode
            // Basic mouse and touch events
            this.stage.on('mousedown touchstart', (e) => {
                // Normalize event for touch devices
                if (e.type === 'touchstart') {
                    // For touch events, we need to ensure evt.button is set
                    if (!e.evt.button && e.evt.button !== 0) {
                        e.evt.button = 0; // Simulate left mouse button for touch
                    }
                }
                
                const handler = tool_handlers[this.active_tool];
                if (handler && handler.mouse_down) {
                    handler.mouse_down(this, e);
                }
            });
            
            // Modified double-click to handle balloon text editing and image loading
            this.stage.on('dblclick dbltap', (e) => {

                //if mouse click only left button (skip this check for touch events)
                if (e.type !== 'dbltap' && e.evt.button !== 0) return; // Only proceed if left mouse button is clicked or it's a double tap
                //! Ballon editing (higher priority than image loading)
                // Check if balloon-related element was clicked
                if (this.active_tool === 'select' && this.selected_object && this.selected_object.getAttr('object_type') === 'balloon' && this.balloon_edit_mode_active === false) {
                    
                    this.edit_balloon_text(this.selected_object);
                    e.cancelBubble = true;
                    return;                    
                }

                //! Standalone text editing
                // Check if standalone text element was clicked
                if (this.active_tool === 'select' && this.selected_object && this.selected_object.getAttr('object_type') === 'standalone_text') {
                    
                    this.active_subtool = 'text';
                    this.text_editing.active = true;
                    this.text_editing.adjust_mode = false;
                    
                    // Update text editing interface with current object properties
                    this.update_ui_from_selected_object();
                    
                    e.cancelBubble = true;
                    return;
                }
                
                //! Image loading
                // Only proceed with image loading if some selection tool is active
                if (this.active_tool !== 'select_panel' &&                     
                    this.active_tool !== 'select') {
                    return;
                }

                if (this.balloon_edit_mode_active) return;
                
                // Only proceed if a panel is selected
                if (!this.selected_panel) return;
                
                // Get adjusted pointer position
                const adjusted_pos = this.get_pointer_position();
                if (!adjusted_pos) return;
                
                // Check if the click is inside the selected panel
                const point = {
                    x: adjusted_pos.x - this.selected_panel.x(),
                    y: adjusted_pos.y - this.selected_panel.y()
                };
                
                // Only load image if click is inside the selected panel
                if (this.is_point_in_polygon(point, this.selected_panel.points)) {
                    this.load_image_to_selected_panel();
                }
            });
            
            // Handle clicks outside canvas
            document.addEventListener('mousedown', (e) => {
                if (!this.stage) return;

                const container = this.stage.container();

                if (!container.contains(e.target) &&
                    !e.target.closest('.vc-color-picker') &&
                    !e.target.closest('.vc-color-wrap') &&
                    !e.target.closest('.vc-saturation-wrap') &&
                    !e.target.closest('.vc-hue-wrap') &&
                    !e.target.closest('.vc-alpha-wrap') &&
                    !e.target.closest('.vc-input-wrap') &&
                    !e.target.closest('#color_picker_container'))
                { 
                    this.close_all_color_pickers();
                }                
                
                if (!container.contains(e.target) && 
                    !e.target.closest('#top_bar') &&
                    !e.target.closest('#context_toolbar') &&
                    !e.target.closest('#left_bar') &&
                    !e.target.closest('#right_bar') &&
                    !e.target.closest('#status_bar') &&
                    !e.target.closest('#balloon_toolbar') &&
                    !e.target.closest('#mouse_context_menu') &&
                    !e.target.closest('[data-toggle-right-bar]') &&
                    !e.target.closest('#pages_navigation') &&
                    !e.target.closest('#ai_generation_panel') &&    
                    !this.show_popup) {

                        this.deselect_panel();
                        this.deselect_object();
                }
            });
            

            const container = this.stage.container();
            container.tabIndex = 1; // Make container focusable
            container.focus();           

            // Prevent deselection when transformer is being used
            // this.transformer.on('transformstart transformend dragstart dragend', (e) => {
            //     e.cancelBubble = true;
            // });

            // this.transformer.on('transformstart', (e) => {
            // });

            // this.transformer.on('transformend', (e) => {
            // });

            let update_pending = false;


            // this.stage.on('dragstart', (e) => {
            //     return;
           
            //     if (e.target.name() === 'panel_image') {
            //         this.show_minimal_transformer(this.selected_object);                 
            //     }
            //     else if (e.target.name() === 'panel_balloon') {
            //         this.hide_transformer(this.selected_object);                     
            //     }
        
            // });

            // this.stage.on('dragend', (e) => {
            // });

           

            // this.transformer.on('transform', (e) => {
            //     if (update_pending) return;

            //     update_pending = true;

            //     requestAnimationFrame(() => {
            //         const node = e.target;
            //         if (!node) return;

            //         this.object_scale_value = node.scaleX();
            //         this.object_rotation_value = node.rotation();

            //         update_pending = false;
            //     });
            // });
            
            // Register transformations (rotation, scale)
            this.transformer.on('transformend', (e) => {
                this.push_to_history('transform_object');
            });

            // Wheel event debounce timer - Keep this for the performing_wheel flag logic
            let wheel_timer = null;

            // Modified wheel event listener using requestAnimationFrame
            this.stage.on('wheel', (e) => {
                e.evt.preventDefault();
                this.performing_wheel = true; // Keep this flag for potential UI indicators

                // Store the latest event data
                this._last_wheel_event = e;

                // Schedule update if none is pending
                if (!this._zoom_update_pending) {
                    this._zoom_update_pending = true;
                    requestAnimationFrame(this.process_zoom_update);
                }

                // Clear and reset the timer to detect when scrolling stops
                clearTimeout(wheel_timer);
                wheel_timer = setTimeout(() => {
                    this.performing_wheel = false;
                    this.update_balloon_toolbar_position(this.selected_object); // U pdate balloon toolbar position after wheel event
                    // Reset last event when wheel stops to prevent accidental processing later? Optional.
                    // this._last_wheel_event = null;
                }, 140); // Keep the timeout for the performing_wheel flag
            });


            //! Add pan with middle mouse button (button 1)
            // Global mouseup listener to handle mouse release outside canvas
            this._middle_mouse_global_handler = (e) => {
                if (e.button === 1 && this.is_space_pan_active) {
                    this.is_space_pan_active = false;
                    this.restore_previous_tool();
                    
                    // Reset drag state
                    this.middle_mouse_drag_state.is_dragging = false;
                    this.middle_mouse_drag_state.last_pos = null;
                    
                    // Remove global mouseup listener
                    document.removeEventListener('mouseup', this._middle_mouse_global_handler);
                    
                    // Prevent default browser behavior
                    e.preventDefault();
                }
            };

            // Listen for mousedown on the canvas container
            container.addEventListener('mousedown', (e) => {
                // Only activate pan if middle mouse button is pressed, pan is not active, and not editing text
                if (e.button === 1 && !this.is_space_pan_active && !this.is_text_editing && this.stage) {
                    this.previous_tool = this.active_tool;
                    this.is_space_pan_active = true;
                    this.temporarily_activate_pan();
                    
                    // Initialize drag state for middle mouse pan
                    this.middle_mouse_drag_state.is_dragging = true;
                    this.middle_mouse_drag_state.last_pos = this.stage.getPointerPosition();
                    
                    // Update cursor to indicate dragging
                    this.stage.container().style.cursor = 'grabbing';
                    
                    // Add global mouseup listener
                    document.addEventListener('mouseup', this._middle_mouse_global_handler);
                    
                    // Prevent default browser behavior (scroll)
                    e.preventDefault();
                }
            });

            // Listen for mousemove on the canvas container
            container.addEventListener('mousemove', (e) => {
                // Handle pan movement while middle mouse button is pressed
                if (this.is_space_pan_active && this.middle_mouse_drag_state.is_dragging && this.middle_mouse_drag_state.last_pos && this.stage) {
                    const pos = this.stage.getPointerPosition();
                    if (!pos) return;
                    
                    // Calculate movement
                    const dx = pos.x - this.middle_mouse_drag_state.last_pos.x;
                    const dy = pos.y - this.middle_mouse_drag_state.last_pos.y;
                    
                    // Apply movement
                    this.stage.x(this.stage.x() + dx);
                    this.stage.y(this.stage.y() + dy);
                    this.stage.batchDraw();
                    
                    // Update last position
                    this.middle_mouse_drag_state.last_pos = pos;
                    
                    // Prevent default browser behavior
                    e.preventDefault();
                }
            });

            // Listen for mouseup on the canvas container
            container.addEventListener('mouseup', (e) => {
                // Only restore tool if pan was activated by middle mouse button
                if (e.button === 1 && this.is_space_pan_active) {
                    this.is_space_pan_active = false;
                    this.restore_previous_tool();
                    
                    // Reset drag state
                    this.middle_mouse_drag_state.is_dragging = false;
                    this.middle_mouse_drag_state.last_pos = null;
                    
                    // Remove global mouseup listener
                    document.removeEventListener('mouseup', this._middle_mouse_global_handler);
                    
                    // Prevent default browser behavior
                    e.preventDefault();
                }
            });
        },

        // New method to handle zoom logic within requestAnimationFrame
        process_zoom_update() {
            if (!this.comic_mode) return; // Only setup on comic mode

            if (!this._last_wheel_event) {
                this._zoom_update_pending = false; // Reset flag if no event stored
                return;
            }

            const e = this._last_wheel_event;
            const pointer_pos = this.stage.getPointerPosition(); // Get pointer position inside rAF

            // Check if shift is pressed and we have a selected object that's not locked
            if (e.evt.shiftKey && this.selected_object && this.selected_object.getAttr('locked') !== true) {
                // Resize the selected object instead of zooming canvas
                const scale_factor = e.evt.deltaY < 0 ? 1.05 : 0.95;
                this.resize_selected_object(scale_factor);
            }
            else {
                // Original zoom behavior if not resizing object
                const zoom_factor = e.evt.deltaY < 0 ?
                1 + this.wheel_zoom_sensitivity :
                1 / (1 + this.wheel_zoom_sensitivity);

                // Apply zoom centered on mouse position
                // Pass pointer_pos obtained within rAF
                this.adjust_zoom(zoom_factor, pointer_pos, true);
            }

            // Reset the flag allowing the next frame to be requested
            this._zoom_update_pending = false;
            // Do not clear _last_wheel_event here, let the timeout handle it or keep the last state
        },

        //! Keys        
        setup_hotkeys() {
            if (!this.comic_mode) return; // Only setup on comic mode

            // Remove ALL existing keyboard event listeners to prevent duplicates
            document.removeEventListener('keyup', (e) => { if (e.key === 'Escape' || e.key === 'Enter') this.deselect_all(); });
            document.removeEventListener('keydown', (e) => {
                if (e.ctrlKey || e.metaKey) {                    
                    if (e.key === 'z') { e.preventDefault(); this.undo(); } 
                    else if (e.key === 'y') { e.preventDefault(); this.redo(); }
                }
            });
            
            // Add ONLY the centralized event listener
            document.addEventListener('keydown', (e) => this.handle_key_event(e, 'down'));
            document.addEventListener('keyup', (e) => this.handle_key_event(e, 'up'));
        },

        // Single unified handler for all keyboard events
        handle_key_event(e, event_type) {

            if (!this.comic_mode) return; // Only handle key events in comic mode

            // Skip ALL canvas hotkeys when focus is in AI panel (let native input behavior work)
            if (e.target.closest('#ai_generation_panel')) {
                return;
            }


            // Build current event key for checking critical hotkeys
            let key = e.key;
            if (key === ' ') key = 'Space';
            const modifiers = [];
            if (e.ctrlKey && key !== 'Control') modifiers.push('ctrl');
            if (e.metaKey && key !== 'Meta') modifiers.push('meta');
            if (e.shiftKey && key !== 'Shift') modifiers.push('shift');
            if (e.altKey && key !== 'Alt') modifiers.push('alt');
            const hotkey = [...modifiers, (key ? key.toLowerCase() : '')].join('+');
            
            // Critical hotkeys that should work even when focus is in form elements
            const critical_hotkeys = [
                'ctrl+s', 'meta+s',  // Save
                'ctrl+z', 'meta+z',  // Undo
                'ctrl+y', 'meta+y',  // Redo
                'ctrl+shift+z', 'meta+shift+z',  // Redo (alternative)
                'ctrl+r', 'meta+r',  // Reload
                'F5'                 // Reload
            ];                      
            
            // Skip hotkeys when focus is in form elements OR when editing text/layer names
            // EXCEPT for critical hotkeys that should always work
            if (((e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') || this.is_text_editing) && 
                !critical_hotkeys.includes(hotkey)) {
                return;
            }
            
            // Skip document-specific hotkeys when welcome screen is shown (no document is open)
            const document_specific_hotkeys = [
                'Delete', 'Backspace', 'f', '+', '=', '-', '0', 'e', 'r', 'Space',
                'ctrl+c', 'meta+c', 'ctrl+v', 'meta+v', 'ctrl+x', 'meta+x',
                'ctrl+z', 'meta+z', 'ctrl+y', 'meta+y', 'ctrl+shift+z', 'meta+shift+z'
            ];
            
            // Check if this is a document-specific hotkey and block it if welcome screen is shown
            if (this.show_welcome_screen && (document_specific_hotkeys.includes(key) || document_specific_hotkeys.includes(hotkey))) {
                return;
            }
            
            // Helper variable for later use
            const ctrl_or_meta = e.ctrlKey || e.metaKey;


            if (key === 'Control' && event_type === 'down' && this.transformer) {
                this.transformer.shouldOverdrawWholeArea(false);
                this.ui_layer.batchDraw();
            }

            if (key === 'Control' && event_type === 'up' && this.transformer) {
                this.transformer.shouldOverdrawWholeArea(true);
                this.ui_layer.batchDraw();
            }

            
            // CENTRALIZED object with ALL hotkeys and their actions
            const hotkey_actions = {
                // Common actions based on event_type + key
                'down:Enter': () => {
                    if (this.is_text_editing) {
                        this.confirm_layer_name_edit();
                        return true;
                    } 
                    // Ensure layer selection is not deselected on Enter
                    if (!this.is_text_editing) {
                        this.deselect_all();
                    }
                    return true;
                },
                
                'down:Escape': () => {
                    if (this.is_text_editing) {
                        this.cancel_layer_name_edit();
                        return true;
                    }
                    
                    this.exit_balloon_edit_mode();
                    this.exit_panel_edit_mode();
                    
                    // Only deselect all when not in text editing mode
                    this.deselect_all();
                    return true;
                },
                
                // Undo/redo operations
                'down:ctrl+z': () => { this.undo(); return true; },
                'down:meta+z': () => { this.undo(); return true; },
                'down:ctrl+y': () => { this.redo(); return true; },
                'down:meta+y': () => { this.redo(); return true; },
                'down:ctrl+shift+z': () => { this.redo(); return true; },
                'down:meta+shift+z': () => { this.redo(); return true; },
                
                // File operations  
                'down:ctrl+alt+n': () => { 
                    this.confirm_new_document(); 
                    return true; 
                },
                'down:meta+alt+n': () => { 
                    this.confirm_new_document(); 
                    return true; 
                },
                'down:ctrl+o': () => { 
                    this.open_document(); 
                    return true; 
                },
                'down:meta+o': () => { 
                    this.open_document(); 
                    return true; 
                },
                'down:ctrl+e': () => { 
                    this.show_export_modal(); 
                    return true; 
                },
                'down:meta+e': () => { 
                    this.show_export_modal(); 
                    return true; 
                },
                
                // Deletion operations
                'down:Delete': () => {
                    if (this.selected_object && !this.selected_object.getAttr('locked')) {
                        this.delete_selected_object();
                        return true;
                    } 
                    
                    // else if (this.selected_panel) {
                    //     this.delete_selected_panel();
                    //     return true;
                    // }
                    return false;
                },
                
                'down:Backspace': () => {
                    // if (this.selected_object && !this.selected_object.getAttr('locked')) {
                    //     this.delete_selected_object();
                    //     return true;
                    // } else if (this.selected_panel) {
                    //     this.delete_selected_panel();
                    //     return true;
                    // }
                    // return false;
                },
                
                // Space for temporary pan
                'down:Space': () => {
                    if (!this.is_space_pan_active && !this.is_text_editing) {
                        this.previous_tool = this.active_tool;
                        this.is_space_pan_active = true;
                        this.temporarily_activate_pan();
                        return true;
                    }
                    return false;
                },
                
                'up:Space': () => {
                    if (this.is_space_pan_active) {
                        this.is_space_pan_active = false;
                        this.restore_previous_tool();
                        return true;
                    }
                    return false;
                },
                
                // Alt key for zoom adjustments
                'down:alt': () => {
                    if (this.active_tool === 'zoom') {
                        this.stage.container().style.cursor = 'zoom-out';
                        return true;
                    }
                    return false;
                },
                
                'up:alt': () => {
                    if (this.active_tool === 'zoom') {
                        this.stage.container().style.cursor = 'zoom-in';
                        return true;
                    }
                    return false;
                },

                'down:ctrl+c': () => { this.copy_selected_object(); return true; },
                'down:meta+c': () => { this.copy_selected_object(); return true; },
                'down:ctrl+v': () => { this.paste_copied_object(); return true; },
                'down:meta+v': () => { this.paste_copied_object(); return true; },
                'down:ctrl+x': () => { this.cut_selected_object(); return true; },
                'down:meta+x': () => { this.cut_selected_object(); return true; },

                // Zoom operations
                'down:+': () => { 
                    this.zoom_in(); 
                    return true; 
                },
                'down:=': () => { 
                    // Handle + key when shift is not pressed (= is + without shift)
                    this.zoom_in(); 
                    return true; 
                },
                'down:-': () => { 
                    this.zoom_out(); 
                    return true; 
                },
                'down:0': () => { 
                    this.reset_position_and_zoom(); 
                    return true; 
                },
                'down:f': () => { 
                    this.fit_zoom_to_panel(); 
                    return true; 
                },

                // Save operations - color pickers already closed above
                'down:ctrl+s': () => { 
                    this.save_document_direct(); 
                    return true; 
                },
                'down:meta+s': () => { 
                    this.save_document_direct(); 
                    return true; 
                },

                // Tool selection hotkeys
                'down:e': () => { 
                    this.change_tool('select'); 
                    return true; 
                },
                'down:r': () => { 
                    this.change_tool('select_panel'); 
                    return true; 
                },

                // View toggles
                'down:v': () => { 
                    this.toggle_pages_navigation(); 
                    return true; 
                },

                // Ctrl key for direct selection override
                // 'down:ctrl': () => {
                //     console.log('Ctrl pressed, transformer should not overdraw whole area:', this.transformer.shouldOverdrawWholeArea());
                //     if (this.transformer) {
                //         this.transformer.shouldOverdrawWholeArea(false);                        
                //         if (this.ui_layer) this.ui_layer.batchDraw(); // Redraw UI layer only
                //         return true; // Indicate handled, but don't prevent default (allows Ctrl+C etc.)
                //     }
                //     return false;
                // },
                // 'up:ctrl': () => {
                //     if (this.transformer) {
                //         this.transformer.shouldOverdrawWholeArea(true);
                //         if (this.ui_layer) this.ui_layer.batchDraw(); // Redraw UI layer only
                //         return true; // Indicate handled
                //     }
                //     return false;
                // },
            };
            
            // Build action key based on event type and hotkey
            const action_key = `${event_type}:${hotkey}`;
            const simple_action_key = `${event_type}:${key}`;

            //console.log('Key event:', action_key, 'Simple:', simple_action_key, 'Event type:', event_type);
            
            // List of hotkeys that should preventDefault FIRST (before calling function)
            const browser_conflict_hotkeys = [
                'down:ctrl+n', 'down:meta+n',  // New window
                'down:ctrl+e', 'down:meta+e',  // Search/Address bar
                'down:ctrl+o', 'down:meta+o',  // Open file
                'down:ctrl+s', 'down:meta+s',  // Save page
                'down:ctrl+w', 'down:meta+w',  // Close tab
                'down:ctrl+t', 'down:meta+t',  // New tab
                'down:ctrl+r', 'down:meta+r',  // Reload page
                'down:F5',                     // Reload page
            ];
            
            // Prevent default FIRST for browser-conflicting hotkeys
            if (browser_conflict_hotkeys.includes(action_key)) {
                //console.log('Preventing default for:', action_key);
                e.preventDefault();
            }
            
            // Try complex hotkey first, then simple key if no modifiers
            if (hotkey_actions[action_key]) {
                //console.log('Executing hotkey action:', action_key);
                const result = hotkey_actions[action_key]();
                
                // Handle async functions (promises)
                if (result instanceof Promise) {
                    result.then((success) => {
                        if (success && !browser_conflict_hotkeys.includes(action_key)) {
                            // Note: preventDefault is already called above for browser conflicts
                        }
                    }).catch((error) => {
                        console.error('Error executing hotkey action:', error);
                    });
                } else if (result) {
                    // Only preventDefault if not already done above
                    if (!browser_conflict_hotkeys.includes(action_key)) {
                        e.preventDefault();
                    }
                }
            } 
            else if (!ctrl_or_meta && !e.shiftKey && !e.altKey && hotkey_actions[simple_action_key]) {
                const result = hotkey_actions[simple_action_key]();
                
                // Handle async functions for simple keys too
                if (result instanceof Promise) {
                    result.then((success) => {
                        if (success) {
                            // preventDefault already handled above if needed
                        }
                    }).catch((error) => {
                        console.error('Error executing simple hotkey action:', error);
                    });
                } else if (result) {
                    e.preventDefault();
                }
            }

            // Handle Ctrl key up/down specifically even if other keys are pressed
            if (key === 'control') {
                const ctrl_action_key = `${event_type}:ctrl`;
                if (hotkey_actions[ctrl_action_key]) {
                    hotkey_actions[ctrl_action_key]();
                    // Don't prevent default here to allow combinations like Ctrl+C
                }
                return; // Stop further processing for Control key itself
            }
        },



        //! Environment and maintenance checks
        async check_maintenance_status() {
            try {
                const form_data = new FormData();
                form_data.append('request', 'get_maintenance_status');
                
                const response = await axios.post('ajax.php', form_data);
                this.maintenance_mode = response.data.maintenance_mode;
            } catch (error) {
                console.error('Error checking maintenance status:', error);
                this.maintenance_mode = false;
            }
        },

        //! Object Management
        move_object_to_global_panel() {
            if (!this.selected_object) {
                this.status_message = this.get_t('status.no_object_selected');
                return;
            }

            // Find the global panel
            const global_panel = this.panels.find(panel => panel.getAttr('is_global') === true);
            if (!global_panel) {
                this.status_message = this.get_t('status.global_panel_not_found');
                return;
            }

            // Find current parent panel
            const current_panel = this.find_parent_panel_for_object(this.selected_object);
            if (!current_panel) {
                this.status_message = this.get_t('status.object_panel_not_found');
                return;
            }

            // Check if object is already in global panel
            if (current_panel.getAttr('is_global') === true) {
                this.status_message = this.get_t('status.object_already_in_global_panel');
                return;
            }

            // Calculate absolute position considering panel position
            const object_pos = this.selected_object.position();
            const panel_pos = current_panel.position();
            const content_group_pos = current_panel.content_group.position();
            
            // Calculate final absolute position
            const absolute_x = panel_pos.x + content_group_pos.x + object_pos.x;
            const absolute_y = panel_pos.y + content_group_pos.y + object_pos.y;
            
            // Remove object from current panel
            this.selected_object.remove();
            
            // Add object to global panel
            global_panel.content_group.add(this.selected_object);
            
            // Set position to maintain visual location
            this.selected_object.position({
                x: absolute_x,
                y: absolute_y
            });

            // Update UI and redraw
            this.redraw();
            this.push_to_history('move_object_to_global_panel');
            this.status_message = this.get_t('status.object_moved_to_global_panel');
        },

        //! Pages navigation visibility toggle
        toggle_pages_navigation() {
            this.show_pages_navigation = !this.show_pages_navigation;
            
            // Force recalculation when visibility changes to adjust layout
            this.$nextTick(() => {
                setTimeout(() => {
                    this.handle_window_resize();
                }, 1);
            });
        },

        // Check for payment success URL parameter and show success modal

        //! Page reload prevention
        setup_page_reload_prevention() {
            if (!this.comic_mode) return; // Only setup on comic mode

            // Add beforeunload event listener to prevent data loss
            this.beforeunload_handler = (event) => {
                // Check if there are unsaved changes
                if (this.has_unsaved_changes()) {
                    const message = this.get_t('dialogs.unsaved_changes_warning');
                    event.preventDefault();
                    event.returnValue = message; // For Chrome
                    return message; // For other browsers
                }
            };

            // Add keydown event listener to prevent F5 and Ctrl+R
            this.keydown_handler = (event) => {
                // Prevent F5 (keyCode 116)
                if (event.keyCode === 116) {
                    if (this.has_unsaved_changes()) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.show_confirmation_popup(
                            this.get_t('dialogs.reload_confirmation_title'),
                            this.get_t('dialogs.reload_confirmation_message'),
                            () => {
                                // User confirmed, allow reload
                                window.location.reload();
                            }
                        );
                        return false;
                    }
                }
                
                // Prevent Ctrl+R (keyCode 82 with ctrlKey)
                if (event.ctrlKey && event.keyCode === 82) {
                    if (this.has_unsaved_changes()) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.show_confirmation_popup(
                            this.get_t('dialogs.reload_confirmation_title'),
                            this.get_t('dialogs.reload_confirmation_message'),
                            () => {
                                // User confirmed, allow reload
                                window.location.reload();
                            }
                        );
                        return false;
                    }
                }
            };

            // Add event listeners
            window.addEventListener('beforeunload', this.beforeunload_handler);
            document.addEventListener('keydown', this.keydown_handler);
        },

        has_unsaved_changes() {
            // Use existing document_has_unsaved_changes variable from document mixin
            return this.document_has_unsaved_changes;
        },

        //! Browser compatibility detection
        detect_browser_compatibility() {
            const user_agent = navigator.userAgent;
            
            // Reset browser info
            this.browser_info = {
                name: 'unknown',
                supports_filesystem_api: false,
                is_firefox: false,
                is_safari: false,
                is_chromium: false
            };
            
            // Detect Firefox
            if (user_agent.includes('Firefox')) {
                this.browser_info.name = 'firefox';
                this.browser_info.is_firefox = true;
                this.browser_info.supports_filesystem_api = false;
                return;
            }
            
            // Detect Safari
            if (user_agent.includes('Safari') && !user_agent.includes('Chrome')) {
                this.browser_info.name = 'safari';
                this.browser_info.is_safari = true;
                this.browser_info.supports_filesystem_api = false;
                return;
            }
            
            // Detect Chromium-based browsers
            if (user_agent.includes('Chrome') || user_agent.includes('Chromium')) {
                this.browser_info.is_chromium = true;
                this.browser_info.name = 'chromium';
                
                // Test filesystem API support
                this.test_filesystem_api_support();
                return;
            }
            
            // Default case - test filesystem API support anyway
            this.test_filesystem_api_support();
        },

        test_filesystem_api_support() {
            
            // Test if FileSystem API is available and functional
            try {
                if (typeof window.showOpenFilePicker === 'function' && 
                    typeof window.showSaveFilePicker === 'function') {
                    this.browser_info.supports_filesystem_api = true;
                } else {
                    this.browser_info.supports_filesystem_api = false;
                }
            } catch (error) {
                this.browser_info.supports_filesystem_api = false;
            }
        },

        //! Get browser compatibility message
        get_browser_compatibility_message() {
            if (this.browser_info.is_firefox) {
                return {
                    type: 'firefox',
                    message: this.get_t('browser.firefox_not_supported'),
                    link: null
                };
            }
            
            if (this.browser_info.is_safari) {
                return {
                    type: 'safari',
                    message: this.get_t('browser.safari_not_supported'),
                    link: null
                };
            }
            
            if (this.browser_info.is_chromium && !this.browser_info.supports_filesystem_api) {
                return {
                    type: 'chromium_disabled',
                    message: this.get_t('browser.chromium_filesystem_disabled'),
                    link: 'https://mikomic.com/docs/#browser-compatibility/1'
                };
            }
            
            return {
                type: 'unknown',
                message: this.get_t('browser.recent_files_disabled_desc'),
                link: null
            };
        },

        //! Check if recent files should be disabled
        should_disable_recent_files() {
            return this.browser_info && !this.browser_info.supports_filesystem_api;
        },

        //! EyeDropper API for screen color picking
        async start_eyedropper(callback) {
            if (!this.comic_mode) return; // Only setup on comic mode
            if ('EyeDropper' in window) {
                try {
                    const eyeDropper = new EyeDropper();
                    const result = await eyeDropper.open();
                    if (callback) {
                        callback(result.sRGBHex);
                    }
                    return result.sRGBHex;
                } catch (error) {
                    // User cancelled or error occurred
                    console.log('EyeDropper cancelled or error:', error);
                    return null;
                }
            } else {
                // Fallback - show message about browser support
                this.show_popup = true;
                this.popup_title = this.get_t('color_picker.eyedropper_not_supported_title');
                this.popup_message = this.get_t('color_picker.eyedropper_not_supported_message');
                return null;
            }
        },


        //! Close all open color pickers
        close_all_color_pickers() {
            // Emit a global event that color pickers can listen for
            document.dispatchEvent(new CustomEvent('close-color-pickers'));
        },

    

        
    }

}
</script>

